import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { t } from "./i18n";
import * as auth from "./authApi";

export function useAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const data = await auth.fetchMe();
      setUser(data.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const signOut = async () => {
    await auth.logout().catch(() => {});
    setUser(null);
  };

  return { user, loading, refresh, signOut, setUser };
}

function AuthShell({ lang, title, children }) {
  const navigate = useNavigate();

  return (
    <section className="if-auth">
      <button type="button" className="if-legal-back" onClick={() => navigate("/")}>
        ← {t(lang, "home")}
      </button>
      <h1>{title}</h1>
      {children}
    </section>
  );
}

function Field({ label, id, type = "text", value, onChange, autoComplete }) {
  return (
    <label className="if-auth-field">
      <span>{label}</span>
      <input id={id} type={type} value={value} onChange={onChange} autoComplete={autoComplete} required />
    </label>
  );
}

function PasswordField({ label, id, value, onChange, autoComplete, lang }) {
  const [show, setShow] = useState(false);

  return (
    <label className="if-auth-field">
      <span>{label}</span>
      <span className="if-auth-passwrap">
        <input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
          required
        />
        <button
          type="button"
          className="if-auth-pass-toggle"
          onClick={() => setShow((v) => !v)}
          aria-label={show ? t(lang, "authHidePassword") : t(lang, "authShowPassword")}
          aria-pressed={show}
        >
          {show ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
        </button>
      </span>
    </label>
  );
}

function AuthMethodTabs({ lang, method, onChange, loginLabel }) {
  return (
    <div className="if-auth-tabs" role="tablist" aria-label={t(lang, loginLabel ? "authLoginWith" : "authSignupWith")}>
      <button
        type="button"
        role="tab"
        aria-selected={method === "email"}
        className={`if-auth-tab${method === "email" ? " if-auth-tab--active" : ""}`}
        onClick={() => onChange("email")}
      >
        {t(lang, "authMethodEmail")}
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={method === "phone"}
        className={`if-auth-tab${method === "phone" ? " if-auth-tab--active" : ""}`}
        onClick={() => onChange("phone")}
      >
        {t(lang, "authMethodMobile")}
      </button>
    </div>
  );
}

export function LoginPage({ lang, onSuccess }) {
  const navigate = useNavigate();
  const [method, setMethod] = useState("phone");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = await auth.login({
        method,
        email: method === "email" ? email.trim() : undefined,
        phone: method === "phone" ? phone : undefined,
        password,
      });
      onSuccess(data.user);
      navigate("/account");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const verifyHref = () => {
    if (method === "phone") {
      const digits = phone.replace(/\D/g, "").slice(-10);
      return `/verify?phone=${encodeURIComponent(digits)}`;
    }
    return `/verify?email=${encodeURIComponent(email.trim())}`;
  };

  return (
    <AuthShell lang={lang} title={t(lang, "authLogin")}>
      <form className="if-auth-form" onSubmit={submit}>
        <AuthMethodTabs lang={lang} method={method} onChange={setMethod} loginLabel />
        <p className="if-auth-hint">{t(lang, "authSeparateAccounts")}</p>
        {method === "email" ? (
          <Field
            label={t(lang, "authEmail")}
            id="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
          />
        ) : (
          <Field
            label={t(lang, "authMobile")}
            id="login-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
          />
        )}
        <PasswordField lang={lang} label={t(lang, "authPassword")} id="login-pass" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
        {error && <p className="if-auth-error" role="alert">{error}</p>}
        {error && error.toLowerCase().includes("verify") && (
          <p className="if-auth-hint">
            <button type="button" className="if-auth-link" onClick={() => navigate(verifyHref())}>
              {t(lang, "authVerify")} →
            </button>
          </p>
        )}
        <button type="submit" className="if-auth-btn" disabled={busy}>{busy ? "…" : t(lang, "authLogin")}</button>
        <div className="if-auth-links if-auth-links--below-btn">
          <button type="button" className="if-auth-link" onClick={() => navigate("/forgot-password")}>{t(lang, "authForgot")}</button>
          <button type="button" className="if-auth-link" onClick={() => navigate("/signup")}>{t(lang, "authSignup")}</button>
        </div>
      </form>
    </AuthShell>
  );
}

export function SignupPage({ lang, onSuccess }) {
  const navigate = useNavigate();
  const [method, setMethod] = useState("phone");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = await auth.signup({
        name,
        password,
        method,
        email: method === "email" ? email : undefined,
        phone: method === "phone" ? phone : undefined,
      });
      if (data.needsVerification) {
        const q = data.method === "phone" || method === "phone"
          ? `phone=${encodeURIComponent(data.phone || phone)}`
          : `email=${encodeURIComponent(data.email || email)}`;
        navigate(`/verify?${q}`);
        return;
      }
      onSuccess(data.user);
      navigate("/account");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell lang={lang} title={t(lang, "authSignup")}>
      <AuthMethodTabs lang={lang} method={method} onChange={setMethod} />
      <p className="if-auth-hint">{t(lang, "authSeparateAccounts")}</p>
      <form className="if-auth-form" onSubmit={submit}>
        <Field label={t(lang, "authName")} id="signup-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        {method === "email" ? (
          <Field label={t(lang, "authEmail")} id="signup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
        ) : (
          <>
            <Field label={t(lang, "authMobile")} id="signup-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
            <p className="if-auth-hint">{t(lang, "authMobileHint")}</p>
          </>
        )}
        <PasswordField lang={lang} label={t(lang, "authPassword")} id="signup-pass" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        {error && <p className="if-auth-error" role="alert">{error}</p>}
        <button type="submit" className="if-auth-btn" disabled={busy}>{busy ? "…" : t(lang, "authCreate")}</button>
      </form>
      <p className="if-auth-links">
        <button type="button" className="if-auth-link" onClick={() => navigate("/login")}>{t(lang, "authHaveAccount")}</button>
      </p>
    </AuthShell>
  );
}

export function ForgotPage({ lang }) {
  const navigate = useNavigate();
  const [method, setMethod] = useState("phone");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const data = await auth.forgotPassword({
        method,
        email: method === "email" ? email.trim() : undefined,
        phone: method === "phone" ? phone : undefined,
      });
      setMsg(data.message);
      if (data.method === "phone" && data.phone) {
        setTimeout(() => navigate(`/reset-password?phone=${encodeURIComponent(data.phone)}`), 1500);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell lang={lang} title={t(lang, "authForgot")}>
      <form className="if-auth-form" onSubmit={submit}>
        <AuthMethodTabs lang={lang} method={method} onChange={setMethod} loginLabel />
        {method === "email" ? (
          <Field label={t(lang, "authEmail")} id="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
        ) : (
          <Field label={t(lang, "authMobile")} id="forgot-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
        )}
        {error && <p className="if-auth-error" role="alert">{error}</p>}
        {msg && <p className="if-auth-success" role="status">{msg}</p>}
        <button type="submit" className="if-auth-btn" disabled={busy}>
          {busy ? "…" : (method === "phone" ? t(lang, "authSendResetCode") : t(lang, "authSendReset"))}
        </button>
      </form>
      <p className="if-auth-links">
        <button type="button" className="if-auth-link" onClick={() => navigate("/login")}>{t(lang, "authBackLogin")}</button>
      </p>
    </AuthShell>
  );
}

export function ResetPage({ lang }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const presetPhone = params.get("phone") || "";
  const [phone, setPhone] = useState(presetPhone);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const phoneMode = Boolean(presetPhone) || (!token && phone);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = phoneMode
        ? await auth.resetPassword({ phone, code, password })
        : await auth.resetPassword({ token, password });
      setMsg(data.message);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!token && !presetPhone) {
    return (
      <AuthShell lang={lang} title={t(lang, "authReset")}>
        <p className="if-auth-error">{t(lang, "authInvalidReset")}</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell lang={lang} title={t(lang, "authReset")}>
      <form className="if-auth-form" onSubmit={submit}>
        {phoneMode && (
          <>
            <Field label={t(lang, "authMobile")} id="reset-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
            <Field label={t(lang, "authResetCode")} id="reset-code" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" />
          </>
        )}
        <PasswordField lang={lang} label={t(lang, "authNewPassword")} id="reset-pass" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        {error && <p className="if-auth-error" role="alert">{error}</p>}
        {msg && <p className="if-auth-success" role="status">{msg}</p>}
        <button type="submit" className="if-auth-btn" disabled={busy}>{busy ? "…" : t(lang, "authSavePassword")}</button>
      </form>
    </AuthShell>
  );
}

export function VerifyPage({ lang }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const presetEmail = params.get("email") || "";
  const presetPhone = params.get("phone") || "";
  const isPhone = Boolean(presetPhone);
  const [email, setEmail] = useState(presetEmail);
  const [phone, setPhone] = useState(presetPhone);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState(
    presetPhone ? t(lang, "authVerifySentSms") : (presetEmail ? t(lang, "authVerifySent") : "")
  );
  const [busy, setBusy] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setBusy(true);
      setError("");
      try {
        const data = await auth.verifyAccount({ token });
        if (!cancelled) {
          setMsg(data.message || t(lang, "authVerifySuccess"));
          setTimeout(() => navigate("/login"), 2000);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, lang, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    const em = email.trim();
    const ph = phone.replace(/\D/g, "").slice(-10);
    if (!em && !ph) {
      setError(t(lang, "authVerifyNeedContact"));
      return;
    }
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const data = await auth.verifyAccount({
        email: em || undefined,
        phone: ph || undefined,
        code,
      });
      setMsg(data.message || t(lang, "authVerifySuccess"));
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    const em = email.trim();
    const ph = phone.replace(/\D/g, "").slice(-10);
    if (!em && !ph) {
      setError(t(lang, "authVerifyNeedContact"));
      return;
    }
    setResendBusy(true);
    setError("");
    try {
      const data = await auth.resendVerify({
        email: em || undefined,
        phone: ph || undefined,
      });
      setMsg(data.message || (ph ? t(lang, "authVerifySentSms") : t(lang, "authVerifySent")));
    } catch (err) {
      setError(err.message);
    } finally {
      setResendBusy(false);
    }
  };

  if (token) {
    return (
      <AuthShell lang={lang} title={t(lang, "authVerify")}>
        {busy && <p className="if-auth-hint">{t(lang, "authVerifyTokenBusy")}</p>}
        {error && <p className="if-auth-error" role="alert">{error}</p>}
        {msg && <p className="if-auth-success" role="status">{msg}</p>}
        {!busy && error && (
          <p className="if-auth-links">
            <button type="button" className="if-auth-link" onClick={() => navigate(`/verify?${presetPhone ? `phone=${encodeURIComponent(presetPhone)}` : `email=${encodeURIComponent(email)}`}`)}>
              {t(lang, "authVerifyCode")}
            </button>
          </p>
        )}
      </AuthShell>
    );
  }

  return (
    <AuthShell lang={lang} title={t(lang, "authVerify")}>
      {msg && <p className="if-auth-success" role="status">{msg}</p>}
      <form className="if-auth-form" onSubmit={submit}>
        {isPhone || phone ? (
          <Field label={t(lang, "authMobile")} id="verify-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
        ) : (
          <Field label={t(lang, "authEmail")} id="verify-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
        )}
        <Field label={t(lang, "authVerifyCode")} id="verify-code" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" />
        {error && <p className="if-auth-error" role="alert">{error}</p>}
        <button type="submit" className="if-auth-btn" disabled={busy}>{busy ? "…" : t(lang, "authVerifySubmit")}</button>
      </form>
      <p className="if-auth-links">
        <button type="button" className="if-auth-link" onClick={resend} disabled={resendBusy}>
          {resendBusy ? t(lang, "authResendBusy") : t(lang, "authResendCode")}
        </button>
        {" · "}
        <button type="button" className="if-auth-link" onClick={() => navigate("/login")}>{t(lang, "authBackLogin")}</button>
      </p>
    </AuthShell>
  );
}

export function AccountPage({ lang, user, onLogout }) {
  const navigate = useNavigate();
  if (!user) {
    navigate("/login");
    return null;
  }
  return (
    <AuthShell lang={lang} title={t(lang, "authAccount")}>
      <dl className="if-auth-dl">
        <dt>{t(lang, "authName")}</dt>
        <dd>{user.name}</dd>
        {user.email && (
          <>
            <dt>{t(lang, "authEmail")}</dt>
            <dd>{user.email}</dd>
          </>
        )}
        {user.phone && (
          <>
            <dt>{t(lang, "authMobile")}</dt>
            <dd>{user.phone}</dd>
          </>
        )}
        <dt>{t(lang, "authMemberSince")}</dt>
        <dd>{new Date(user.created_at).toLocaleDateString(lang === "ta" ? "ta-IN" : "en-IN", { year: "numeric", month: "long", day: "numeric" })}</dd>
      </dl>
      <button type="button" className="if-auth-btn if-auth-btn--ghost" onClick={async () => { await onLogout(); navigate("/"); }}>
        {t(lang, "authLogout")}
      </button>
    </AuthShell>
  );
}
